'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Box } from '@mui/material';
import { SideNav } from './SideNav';
import { RouteLoadingIndicator } from './RouteLoadingIndicator';
import { useNav } from '@/contexts/NavContext';
import { STORAGE_CONFIG } from '@/config/app';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Main Application Layout Component
 * Provides responsive layout with side nav
 */
export function AppLayout({ children }: AppLayoutProps) {
  const pathname = usePathname();
  const { isExpanded } = useNav();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Update body class based on nav expanded state
  useEffect(() => {
    if (isExpanded) {
      document.body.classList.remove('nav-collapsed');
    } else {
      document.body.classList.add('nav-collapsed');
    }
  }, [isExpanded]);

  // Load drawer state from localStorage on mount
  React.useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_CONFIG.keys.drawerOpen);
    if (savedState !== null) {
      setDrawerOpen(savedState === 'true');
    }
  }, []);

  const handleDrawerToggle = () => {
    const newState = !drawerOpen;
    setDrawerOpen(newState);
    localStorage.setItem(STORAGE_CONFIG.keys.drawerOpen, String(newState));
  };

  const isAuthRoute = pathname?.startsWith('/auth');

  if (isAuthRoute) {
    return <>{children}</>;
  }

  return (
    <Box className="app-layout-root">
      <RouteLoadingIndicator />

      {/* Side Navigation - Always visible, permanent variant */}
      <Box className="app-layout-sidebar">
        <SideNav open={true} onClose={handleDrawerToggle} variant="permanent" />
      </Box>

      {/* Main Content Area */}
      <Box component="main" className="app-layout-main">
        {children}
      </Box>
    </Box>
  );
}
